/**
 * SDK Shadow DOM scoped CSS(样式隔离三招合体)
 *
 * 1. :host all:initial 切断宿主继承(第 1 招)
 * 2. 自有 token 在 Shadow DOM 内重声明(第 2 招),镜像 hotel-hub base.css 品牌色/圆角
 * 3. 完整组件样式自包含(第 3 招的等价:不复用 Tailwind 类,故不注入 globals.css)
 *
 * 主题适配按设计"后做";当前 token 取 hotel-hub default 预设值,视觉一致。
 * 类名统一 hh- 前缀(Shadow DOM 已隔离,前缀仅为可读性)。
 */
export const SDK_STYLE = `
  :host {
    all: initial;
    display: block;
    /* 宿主作为 flex item(如展示页 flex justify-center)时,宽度按内容而定;
       房券名 nowrap 等内容会撑大固有宽度导致溢出宿主容器--max-width:100% 把宿主
       封顶在容器宽度内,使内部卡片获得确定宽度、文字正常省略/换行(窄于容器时仍居中) */
    max-width: 100%;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, 'PingFang SC',
      'Microsoft YaHei', sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: var(--hh-foreground);
    box-sizing: border-box;
  }
  :host([hidden]) { display: none; }
  *,
  *::before,
  *::after { box-sizing: border-box; }

  /* ===== token(镜像 hotel-hub base.css default 预设) ===== */
  :host {
    --hh-brand-primary: #FF383C;
    --hh-brand-accent: #0084ff;
    --hh-foreground: hsl(0 0% 3.9%);
    --hh-muted: hsl(0 0% 45.1%);
    --hh-card: #ffffff;
    --hh-bg: #f5f5f5;
    --hh-border: hsl(0 0% 89.8%);
    --hh-radius-card: 0.875rem;
    --hh-radius-card-inner: 0.75rem;
    --hh-radius-full: 9999px;
    --hh-shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  }

  .hh-root {
    width: 100%;
  }

  /* ===== 页面头 ===== */
  .hh-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }
  .hh-header__title {
    font-size: 24px;
    font-weight: 400;
    color: var(--hh-foreground);
    margin: 0;
  }

  /* ===== 营销横幅(红包 banner 图) ===== */
  .hh-banner {
    display: block;
    width: 100%;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    transition: opacity 0.15s;
    margin-bottom: 24px;
  }
  .hh-banner:hover { opacity: 0.95; }
  .hh-banner__img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: var(--hh-radius-card);
  }

  /* ===== 通用态 ===== */
  .hh-loading,
  .hh-error {
    padding: 12px 16px;
  }
  .hh-error { color: #b91c1c; }

  /* ===== DateBar ===== */
  .hh-datebar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  .hh-header .hh-pill { padding: 12px 20px; border-radius: 0; }
  .hh-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 16px 32px;
    border: 1px solid var(--hh-border);
    border-radius: var(--hh-radius-full);
    background: var(--hh-card);
    color: var(--hh-foreground);
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.15s;
  }
  .hh-pill:hover { background: hsl(0 0% 96.1%); }
  .hh-pill__date { font-weight: 700; }
  .hh-pill__weekday { color: var(--hh-muted); }
  .hh-pill__nights { color: var(--hh-muted); margin-left: 8px; }
  .hh-pill__chevron { color: var(--hh-muted); }
  .hh-pill__tail { display: inline-flex; align-items: center; gap: 8px; }

  .hh-action {
    padding: 14px 32px;
    border: none;
    border-radius: var(--hh-radius-full);
    background: #000000;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .hh-action:hover { opacity: 0.9; }

  /* ===== 日历浮层(absolute,不 Portal,留在 Shadow DOM 内) ===== */
  .hh-cal-wrap { position: relative; }
  .hh-cal-popover {
    position: absolute;
    top: calc(100% + 8px);
    /* 默认右锚:浮层右缘对齐 pill 右缘,向左展开(pill 在 header 右侧时不溢出右屏);
       --left 改为左锚向右展开(pill 在左侧 date-bar/book-bar 时),方向由 DateBar JS 按视口实测决定 */
    right: 0;
    z-index: 50;
    background: var(--hh-card);
    border-radius: var(--hh-radius-card);
    box-shadow: var(--hh-shadow-lg);
    padding: 16px;
  }
  .hh-cal-popover--left { right: auto; left: 0; }
  /* 日历底部弹层遮罩/拖拽条/关闭按钮:仅 H5 显示(见 styles.h5.ts),桌面隐藏 */
  .hh-cal-backdrop,
  .hh-cal-handle,
  .hh-cal-close { display: none; }
  .hh-cal { display: flex; gap: 24px; }
  .hh-cal__month { width: 280px; }
  .hh-cal__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-weight: 600;
  }
  .hh-cal__nav {
    width: 28px;
    height: 28px;
    border: 1px solid var(--hh-border);
    border-radius: var(--hh-radius-full);
    background: var(--hh-card);
    cursor: pointer;
    color: var(--hh-foreground);
  }
  .hh-cal__nav:disabled { opacity: 0.35; cursor: not-allowed; }
  .hh-cal__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    color: var(--hh-muted);
    font-size: 12px;
    margin-bottom: 4px;
  }
  .hh-cal__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
  }
  .hh-cal__day {
    height: 36px;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    color: var(--hh-foreground);
    font-size: 13px;
  }
  .hh-cal__day:hover:not(:disabled) { background: hsl(0 0% 96.1%); }
  .hh-cal__day:disabled { color: hsl(0 0% 80%); cursor: not-allowed; }
  .hh-cal__day--other { visibility: hidden; }
  .hh-cal__day--in-range { background: #F7F7F7; border-radius: 0; }
  .hh-cal__day--start { border-radius: 8px 0 0 8px; }
  .hh-cal__day--end { border-radius: 0 8px 8px 0; }
  .hh-cal__day--start,
  .hh-cal__day--end,
  .hh-cal__day--single {
    background: #000000;
    color: #fff;
    font-weight: 700;
  }
  .hh-cal__day--single { border-radius: 8px; }

  /* ===== PackageList ===== */
  .hh-list { display: flex; flex-direction: column; gap: 24px; }
  .hh-list__loading,
  .hh-list__empty,
  .hh-list__error {
    padding: 48px 0;
    text-align: center;
    color: var(--hh-muted);
  }
  .hh-list__retry {
    margin-left: 8px;
    color: var(--hh-brand-accent);
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
  }

  /* ===== 查看更多房型 ===== */
  .hh-view-more {
    display: block;
    width: 100%;
    margin-top: 24px;
    padding: 12px;
    border: none;
    background: transparent;
    color: var(--hh-muted);
    font-size: 14px;
    text-align: center;
    cursor: pointer;
    transition: color 0.15s;
  }
  .hh-view-more:hover { color: var(--hh-foreground); }

  /* ===== RoomCard ===== */
  .hh-room {
    background: var(--hh-card);
    border-radius: 0;
    padding: 24px;
    box-shadow: 0px 4px 34px 12px rgba(0, 0, 0, 0.06);
    // border: 1px solid var(--hh-border)
  }
  .hh-room__name { font-size: 18px; font-weight: 700; margin: 0 0 16px; }
  /* 可点击房间名(有设施时用 button 承载):重置 button 默认样式 + flex 对齐箭头;
     font-size/font-weight/margin 由 .hh-room__name 提供(不在此覆盖,保留下间距);
     不加 hover 变色(需求要求) */
  .hh-room__name--link {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 0;
    border: none;
    background: none;
    text-align: left;
    color: inherit;
    font-family: inherit;
    cursor: pointer;
  }
  .hh-room__name-arrow {
    flex-shrink: 0;
    color: var(--hh-muted);
  }
  .hh-room__body { display: flex; gap: 18px; flex-wrap: nowrap; }
  .hh-room__media {
    flex: 60 1 0%;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  /* 左侧图片轮播容器:取代原单图尺寸角色(274px 高),对齐主站房卡 ImageCarousel 视觉 */
  .hh-room__carousel {
    position: relative;
    width: 100%;
    height: 274px;
    overflow: hidden;
    border-radius: 0;
    background: var(--hh-bg);
  }
  .hh-room__carousel-track {
    display: flex;
    height: 100%;
    transition: transform 0.3s ease;
  }
  .hh-room__img {
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
    background: var(--hh-bg);
    -webkit-user-drag: none;
  }
  /* 左右箭头:默认隐藏,hover 容器时显示(对齐主站 group-hover) */
  .hh-room__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: var(--hh-radius-full);
    background: rgba(255, 255, 255, 0.9);
    color: hsl(0 0% 25%);
    box-shadow: 0 1px 4px rgb(0 0 0 / 0.15);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s, background 0.15s;
  }
  .hh-room__carousel:hover .hh-room__nav { opacity: 1; }
  .hh-room__nav:hover { background: #fff; }
  .hh-room__nav--prev { left: 8px; }
  .hh-room__nav--next { right: 8px; }
  /* 底部圆点指示器(对齐主站 CarouselDots:激活态拉长为胶囊) */
  .hh-room__dots {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 8px;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .hh-room__dot {
    width: 6px;
    height: 6px;
    padding: 0;
    border: none;
    border-radius: var(--hh-radius-full);
    background: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: width 0.2s, background 0.2s;
  }
  .hh-room__dot--active {
    width: 16px;
    background: #fff;
  }
  .hh-room__bed { color: var(--hh-foreground); font-size: 16px; font-weight: 600; margin: 12px 0 0; }
  /* 套餐区最小高度与左侧图片对齐(274px):单套餐 flex:1 占满;多套餐自然高度但不低于图片 */
  .hh-room__plans { flex: 85 1 0%; min-width: 0; min-height: 274px; display: flex; flex-direction: column; gap: 16px; }
  .hh-plan {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border: 1px solid var(--hh-border);
    border-radius: 0;
    padding: 20px;
  }
  /* 单套餐卡片高度与左侧图片一致;多套餐单卡最小高度 129px */
  .hh-plan--single { min-height: 274px; }
  .hh-plan--multiple { min-height: 129px; }
  .hh-plan__body {
    flex: 1;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
  }
  .hh-plan__left { flex: 1; min-width: 0; }
  .hh-plan__right {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    flex-shrink: 0;
  }
  /* 单套餐时标签纵向堆叠,多套餐保持横向(默认 flex-wrap) */
  .hh-plan--single .hh-plan__tags {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  /* 单套餐:标签靠左上,价格模块+按钮靠右下(底部对齐) */
  .hh-plan--single .hh-plan__body { align-items: stretch; }
  .hh-plan--single .hh-plan__left {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  /* 单套餐:价格+按钮靠底部(可订/不可订均横向同一行) */
  .hh-plan--single .hh-plan__right { align-items: flex-end; }
  /* 多套餐:左栏与顶部对齐,避免右侧高度影响标题与标签间距 */
  .hh-plan--multiple .hh-plan__left { margin-bottom:6px; }
  .hh-plan__brief {
    font-size: 16px;
    font-weight: 600;
    color: var(--hh-foreground);
    margin: 0;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    /* 固定单行主轴高度(=line-height*font-size=1.4em),切断套餐名行数对
       .hh-plan__body(flex:1) 的零和传导:即使 nowrap 失效换行,brief 也只占单行高,
       多余行被 overflow 裁掉,body 高度不再随套餐名长度抖动。nowrap 下与原视觉一致。
       H5 font-size 14px,1.4em 自动随字号缩放,无需 H5 覆盖。 */
    flex: 0 0 1.4em;
  }
  .hh-plan__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    // margin-top: 12px;
  }
  .hh-plan__tag {
    display: inline-flex;
    align-items: center;
    color: var(--hh-muted);
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
  }
  /* 早餐/支付方式:图标+纯文字 */
  .hh-plan__tag--plain {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    border-radius: 0;
    color: var(--hh-muted);
  }
  /* 含早餐:绿色(对齐主站套餐列表页);无早沿用 --plain 的 muted */
  .hh-plan__tag--breakfast { color: lab(59.0978% -58.6621 41.2579); }
  .hh-plan__tag--danger {
    border: none;
    border-radius: 4px;
    color: var(--hh-brand-primary);
    padding: 1px 4px;
  }
  .hh-plan__tag--success {
    // border-color: color-mix(in oklab, #2aa515, white 60%);
    color: lab(59.0978% -58.6621 41.2579);
  }
  .hh-plan__price { text-align: right; }
  .hh-plan__price-main { display: flex; align-items: flex-end; justify-content: flex-end; gap: 6px; color: var(--hh-brand-primary); }
  .hh-plan__price-badge {
    display: inline-flex;
    align-items: center;
    // padding: 2px 8px;
    border-radius: 9999px 9999px 0 9999px;
    // background: var(--hh-brand-primary);
    // color: #fff;
    color: var(--hh-brand-primary);
    font-size: 14px;
    line-height: 1.4;
  }
  .hh-plan__price-val {
    display: inline-flex;
    align-items: baseline;
    gap: 1px;
  }
  .hh-plan__price-symbol {
    font-size: 16px;
    font-weight: 700;
    color: var(--hh-brand-primary);
    line-height: 1.2;
  }
  .hh-plan__price-num {
    position: relative;
    display: inline-block;
    font-size: 32px;
    font-weight: 700;
    color: var(--hh-brand-primary);
    line-height: 1;
  }
  .hh-plan__price-original { font-size: 12px; color: var(--hh-muted); text-decoration: line-through; }
  .hh-plan__price-unit {
    position: absolute;
    left: 0;
    bottom: 100%;
    font-size: 12px;
    line-height: 1.2;
    font-weight: 400;
    white-space: nowrap;
    color: var(--hh-muted);
  }
  .hh-plan__book {
    padding: 6px 10px;
    border: none;
    border-radius: 0;
    background: linear-gradient(270deg, #FF383C 0%, #FF8080 100%);
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    /* 固定宽度=「立即预订」自然宽(4 个全角字 4×14px + 左右 padding 20px = 76px),
       使「不可订」(3 字)按钮等宽对齐,CJK 全角字宽固定 1em 故无溢出风险 */
    width: 76px;
    text-align: center;
  }
  .hh-plan__book:disabled { background: rgba(0, 0, 0, 0.4); cursor: not-allowed; }
  .hh-plan__hint { margin-top: 8px; color: #ff8d28; font-size: 12px; }
  .hh-plan__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    align-self: flex-end;
    padding: 4px 0;
    border: none;
    background: transparent;
    color: var(--hh-muted);
    font-size: 14px;
    cursor: pointer;
    transition: color 0.15s;
  }
  .hh-plan__toggle:hover { color: var(--hh-foreground); }
  .hh-plan__toggle-icon--up { transform: rotate(180deg); }
  .hh-plan__toggle-icon--down { transform: rotate(0deg); }

  /* ===== 房券列表(特价预售) ===== */
  .hh-voucher { margin-top: 24px; margin-bottom: 52px; }
  .hh-voucher__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
  .hh-voucher__title { font-size: 24px; font-weight: 400; margin: 0; }
  .hh-voucher__title--accent { color: var(--hh-brand-primary); }
  .hh-voucher__title--main { color: var(--hh-foreground); }
  .hh-voucher__meta {
    display: flex;
    align-items: center;
    gap: 16px;
    color: var(--hh-muted);
    font-size: 13px;
  }
  .hh-voucher__meta-item { display: inline-flex; align-items: center; gap: 4px; }

  .hh-voucher__list { display: flex; flex-direction: column; gap: 16px; }

  .hh-voucher__card {
    background: var(--hh-card);
    /* 直角卡片 + 图文无内边距(图片满贴卡片左/上/下缘,内容区 .hh-voucher__main 自带 padding) */
    border-radius: 0;
    padding: 0;
    box-shadow: 0px 4px 34px 12px rgba(0, 0, 0, 0.06);
    /* 整卡可点进详情(role=button),指针提示可点击;「抢购」按钮区独立交互 */
    cursor: pointer;
    /* 兜底:内部任何溢出(长名/长 token)裁在卡片内,不撑破卡片宽度 */
    overflow: hidden;
  }
  .hh-voucher__card-body { display: flex; gap: 16px; min-width: 0; }
  .hh-voucher__media { flex: 0 0 280px; width: 280px; }
  .hh-voucher__img {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border-radius: 0;
    background: var(--hh-bg);
    display: block;
    -webkit-user-drag: none;
  }
  .hh-voucher__img--empty { background: var(--hh-bg); }

  .hh-voucher__main {
    flex: 1 1 0%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    /* 卡片 padding 已移除以让图片满贴,内容区自带内边距(左缘由 card-body gap 承担间距) */
    padding: 16px 16px 16px 0;
  }
  .hh-voucher__title-row { display: flex; align-items: center; gap: 8px; }
  .hh-voucher__tag {
    flex-shrink: 0;
    background: linear-gradient(90deg, #FF383C 0%, #FF006E 100%);
    color: #fff;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
  }
  /* 标题展示单元:整卡可点进详情,此处为视觉单元(flex 布局 + 箭头指示可跳转;hover 减淡引导)。
     flex:1 1 0 让宽度由容器分配(占满标签与已售之间),长标题在此宽度内省略,
     已售固定在右不被挤出;旧 flex:0 1 auto 的 basis:auto 取内容宽,长标题撑大挤走已售 */
  .hh-voucher__title-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex: 1 1 0;
    overflow: hidden;
    color: var(--hh-foreground);
    transition: opacity 0.15s;
  }
  .hh-voucher__title-btn:hover { opacity: 0.8; }
  /* 标题箭头固定宽度不收缩,把空间让给名称省略 */
  .hh-voucher__title-btn svg { flex-shrink: 0; }
  /* 已售数量:标题行右侧,固定宽不增长不收缩(由 title-btn flex:1 占满中间将其推至行尾) */
  .hh-voucher__sold {
    flex: 0 0 auto;
    color: var(--hh-muted);
    font-size: 12px;
    white-space: nowrap;
  }
  .hh-voucher__name {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    /* flex:0 1 auto 紧贴内容宽度(箭头紧跟标题,不占满 title-btn),长标题收缩省略;
       配合 min-width:0 + nowrap + overflow:hidden,长名稳定截断不撑宽卡片 */
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hh-voucher__brief {
    margin: 8px 0 0;
    color: var(--hh-muted);
    font-size: 14px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    /* 长 token(网址/长英文)强制换行,不撑宽卡片 */
    overflow-wrap: break-word;
    word-break: break-word;
  }
  /* 退款标签:绿色(复用套餐 success 标签同色) */
  .hh-voucher__refund { margin: 8px 0 0; font-size: 14px; color: lab(59.0978% -58.6621 41.2579); }

  .hh-voucher__foot {
    margin-top: 16px;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 12px;
  }
  .hh-voucher__price {
    display: flex;
    align-items: baseline;
    color: var(--hh-brand-primary);
  }
  .hh-voucher__price-badge { font-size: 12px; margin-right: 2px; }
  .hh-voucher__price-symbol { font-size: 16px; font-weight: 700; margin-right: 2px; }
  .hh-voucher__price-num { font-size: 24px; font-weight: 700; line-height: 1; }
  .hh-voucher__price-unit { font-size: 12px; }
  /* 抢购按钮:渐变底胶囊(仅改背景为渐变,形状沿用胶囊) */
  .hh-voucher__book {
    padding: 6px 16px;
    border: none;
    border-radius: var(--hh-radius-full);
    background: linear-gradient(270deg, #FF383C 0%, #FF8080 100%);
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.15s;
  }
  .hh-voucher__book:hover { opacity: 0.9; }

  .hh-voucher__more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    margin-top: 16px;
    padding: 8px;
    border: none;
    background: transparent;
    color: var(--hh-muted);
    font-size: 14px;
    cursor: pointer;
    transition: color 0.15s;
  }
  .hh-voucher__more:hover { color: var(--hh-foreground); }

  /* ===== 房间详情弹窗(PC 居中 Modal;H5 底部抽屉见 styles.h5.ts) ===== */
  .hh-modal__backdrop {
    position: fixed;
    inset: 0;
    z-index: 60;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgb(0 0 0 / 0.6);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
  }
  .hh-modal__backdrop[data-state='open'] { animation: hh-modal-fade-in 0.2s ease-out; }
  .hh-modal__card {
    position: relative;
    z-index: 61; /* > backdrop(60),弹框在遮罩之上 */
    display: flex;
    flex-direction: column;
    width: 90vw;
    max-width: 950px;
    height: 80vh;
    max-height: 700px;
    overflow: hidden;
    background: var(--hh-card);
    border-radius: var(--hh-radius-card);
    box-shadow: 0 10px 40px rgb(0 0 0 / 0.2);
  }
  .hh-modal__card[data-state='open'] { animation: hh-modal-pop-in 0.2s ease-out; }
  .hh-modal__header {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    height: 66px;
    padding: 0 24px;
    border-bottom: 1px solid var(--hh-border);
  }
  .hh-modal__title {
    width: 100%;
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    color: var(--hh-foreground);
    margin: 0;
  }
  /* 关闭按钮:PC 在 header 左侧(对齐主流程桌面);H5 覆盖为右上角(见 styles.h5.ts) */
  .hh-modal__close {
    position: absolute;
    left: 24px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: var(--hh-radius-full);
    background: transparent;
    color: var(--hh-foreground);
    cursor: pointer;
    transition: background 0.15s;
  }
  .hh-modal__close:hover { background: hsl(0 0% 95%); }
  .hh-modal__main {
    flex: 1;
    display: flex;
    min-height: 0;
  }
  .hh-modal__facilities {
    width: 50%;
    overflow-y: auto;
    padding: 24px;
  }
  .hh-modal__group { margin-bottom: 24px; }
  .hh-modal__group:last-child { margin-bottom: 0; }
  .hh-modal__group-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--hh-foreground);
    margin: 0 0 12px;
  }
  .hh-modal__group-items {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .hh-modal__facility {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--hh-muted);
  }
  .hh-modal__facility-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: hsl(0 0% 60%);
  }
  img.hh-modal__facility-icon { object-fit: contain; }
  .hh-modal__images {
    width: 50%;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .hh-modal__image {
    width: 100%;
    height: auto;
    display: block;
    border-radius: var(--hh-radius-card);
  }
  .hh-modal__image-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 4 / 3;
    background: var(--hh-bg);
    color: hsl(0 0% 60%);
    border-radius: var(--hh-radius-card);
  }
  @keyframes hh-modal-fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes hh-modal-pop-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }

  /* ===== 登录弹框(.hh-modal__card--login,PC 居中窄卡;H5 底部抽屉见 styles.h5.ts) ===== */
  .hh-modal__card--login {
    width: 420px;
    max-width: calc(100vw - 2rem);
    height: auto;
    max-height: 90vh;
    overflow-y: auto;
  }
  .hh-login__close {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: var(--hh-radius-full);
    background: transparent;
    color: var(--hh-foreground);
    cursor: pointer;
    transition: background 0.15s;
  }
  .hh-login__close:hover { background: hsl(0 0% 95%); }
  .hh-login__body { padding: 40px 32px 32px; }
  .hh-login__title { font-size: 22px; font-weight: 700; margin: 0 0 28px; }
  .hh-login__form { display: flex; flex-direction: column; gap: 16px; }
  .hh-login__field {
    display: flex;
    align-items: center;
    height: 52px;
    border: 1px solid var(--hh-border);
    border-radius: 12px;
    padding: 0 14px;
  }
  .hh-login__input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 15px;
    color: var(--hh-foreground);
  }
  .hh-login__send {
    flex-shrink: 0;
    border: none;
    background: none;
    color: var(--hh-brand-accent);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    padding: 0 4px;
  }
  .hh-login__send:disabled { color: var(--hh-muted); cursor: not-allowed; }
  .hh-login__agree {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    color: hsl(0 0% 40%);
    line-height: 1.5;
    margin: 4px 0 8px;
  }
  .hh-login__check {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    border: 1px solid hsl(0 0% 80%);
    border-radius: var(--hh-radius-full);
    background: transparent;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .hh-login__check--on { background: var(--hh-brand-accent); border-color: var(--hh-brand-accent); }
  .hh-login__error { margin: 0; color: #b91c1c; font-size: 13px; }
  .hh-login__submit {
    height: 52px;
    margin-top: 8px;
    border: none;
    border-radius: var(--hh-radius-full);
    background: var(--hh-brand-accent);
    color: #fff;
    font-size: 17px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .hh-login__submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .hh-login__submit:hover:not(:disabled) { opacity: 0.9; }
  .hh-login__captcha { padding: 40px 24px 32px; display: flex; flex-direction: column; align-items: center; }
  .hh-login__captcha-title { font-size: 18px; font-weight: 600; margin: 0 0 20px; }

  /* ===== 红包弹框(.hh-modal__card--coupon,PC 居中窄卡;H5 底部抽屉见 styles.h5.ts) ===== */
  .hh-modal__card--coupon {
    width: 420px;
    max-width: calc(100vw - 2rem);
    height: auto;
    min-height: 240px;
    max-height: min(600px, 85vh);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .hh-coupon-modal__close {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: none;
    border-radius: var(--hh-radius-full);
    background: transparent;
    color: var(--hh-foreground);
    cursor: pointer;
    transition: background 0.15s;
  }
  .hh-coupon-modal__close:hover { background: hsl(0 0% 95%); }
  .hh-coupon-modal__title {
    flex-shrink: 0;
    padding: 20px 24px 12px;
    font-size: 18px;
    font-weight: 600;
    text-align: center;
    margin: 0;
  }
  /* Banner 位:固定在列表上方,不随滚动(接口返回头图 head_image_pc_url/head_image_mobile_url 时渲染) */
  .hh-coupon-modal__banner { flex-shrink: 0; width: 100%; padding: 0 20px; }
  .hh-coupon-modal__banner-img { display: block; width: 100%; height: auto; border-radius: 8px; }
  .hh-coupon-modal__banner-img--mobile { display: none; }
  /* 券列表滚动区:红包多时仅此区域滚动(固定高度由卡片 flex 撑开) */
  .hh-coupon-modal__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 20px;
    display: flex;
    flex-direction: column;
    -webkit-overflow-scrolling: touch;
  }
  /* 底部按钮区:固定在卡片底部,不随滚动 */
  .hh-coupon-modal__footer {
    flex-shrink: 0;
    padding: 12px 20px 20px;
    background: var(--hh-card);
  }
  .hh-coupon-modal__footer .hh-coupon-modal__submit { margin-top: 0; }
  .hh-coupon-modal__loading,
  .hh-coupon-modal__empty {
    margin: auto 0;
    padding: 48px 0;
    text-align: center;
    color: var(--hh-muted);
    font-size: 14px;
  }
  .hh-coupon-list { display: flex; flex-direction: column; gap: 12px; }
  .hh-coupon-modal__submit {
    width: 100%;
    height: 48px;
    margin-top: 0;
    border: none;
    border-radius: var(--hh-radius-full);
    background: linear-gradient(270deg, #FF383C 0%, #FF8080 100%);
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .hh-coupon-modal__submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .hh-coupon-modal__submit:hover:not(:disabled) { opacity: 0.9; }
  .hh-coupon-modal__submit--outline {
    background: transparent;
    color: var(--hh-foreground);
    border: 1px solid var(--hh-border);
  }

  /* 票券式券卡:左段品牌色优惠值 + 虚线齿孔 + 右段信息 + 已领取标 */
  .hh-coupon-card {
    display: flex;
    overflow: hidden;
    border-radius: 8px;
    background: var(--hh-card);
    box-shadow: 0 1px 4px rgb(0 0 0 / 0.06);
  }
  .hh-coupon-card--claimed { opacity: 0.6; }
  .hh-coupon-card__value {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 96px;
    padding: 12px 8px;
    background: linear-gradient(135deg, #FF383C 0%, #FF6B6B 100%);
    color: #fff;
    text-align: center;
  }
  .hh-coupon-card__big { font-size: 20px; font-weight: 700; line-height: 1.2; }
  .hh-coupon-card__threshold { font-size: 11px; margin-top: 4px; opacity: 0.9; }
  .hh-coupon-card__tag { font-size: 10px; margin-top: 2px; opacity: 0.8; }
  .hh-coupon-card__body {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    border-left: 2px dashed var(--hh-border);
    padding: 12px 14px;
  }
  .hh-coupon-card__info { flex: 1; min-width: 0; }
  .hh-coupon-card__name {
    font-size: 14px;
    font-weight: 500;
    color: var(--hh-foreground);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hh-coupon-card__validity { font-size: 12px; color: var(--hh-muted); margin: 4px 0 0; }
  .hh-coupon-card__badge {
    flex-shrink: 0;
    font-size: 12px;
    color: var(--hh-muted);
    background: hsl(0 0% 96%);
    border-radius: var(--hh-radius-full);
    padding: 3px 8px;
  }

  /* 领取结果 */
  .hh-coupon-result { margin: auto 0; padding: 24px 0; text-align: center; }
  .hh-coupon-result__title { font-size: 18px; font-weight: 600; color: var(--hh-foreground); margin: 0 0 12px; }
  .hh-coupon-result__desc { font-size: 14px; color: var(--hh-muted); margin: 0 0 20px; }
  .hh-coupon-result__failed { list-style: none; padding: 0; margin: 0 0 20px; font-size: 12px; color: var(--hh-muted); }
  .hh-coupon-result__actions { display: flex; gap: 12px; }
  .hh-coupon-result__actions .hh-coupon-modal__submit { margin-top: 0; }

  /* ===== 滑块验证码(从 LazySlideCaptcha.css 转入,shadow 隔离类名保留) ===== */
  .lazy-slide-captcha { --captcha-text-color: #333; user-select: none; }
  .lazy-slide-captcha__main { position: relative; overflow: hidden; background: rgb(244 245 246); }
  .lazy-slide-captcha__background { display: block; width: 100%; }
  .lazy-slide-captcha__slider { position: absolute; top: 0; display: block; height: 100%; }
  .lazy-slide-captcha__message { position: absolute; inset: 0; z-index: 2; display: flex; align-items: center; justify-content: center; flex-direction: column; background-color: rgba(34 34 34 / 0.85); }
  .lazy-slide-captcha__message--loading { background-color: rgb(244 245 246); }
  .lazy-slide-captcha__message-icon { width: 28px; height: 28px; margin: 0 auto; }
  .lazy-slide-captcha__loading-icon { width: 24px; height: 24px; border: 2px solid rgba(118 198 29 / 0.25); border-top-color: #76c61d; border-radius: 50%; animation: lazy-slide-captcha-turn 1s linear infinite; }
  .lazy-slide-captcha__message-text { display: inline-block; max-width: 220px; padding: 10px; color: #fff; text-align: center; font-size: 14px; }
  .lazy-slide-captcha__message--loading .lazy-slide-captcha__message-text { color: rgb(150 150 150); }
  .lazy-slide-captcha__bar { position: relative; overflow: hidden; margin-top: 5px; text-align: center; }
  .lazy-slide-captcha__progress { position: absolute; left: 0; top: 0; }
  .lazy-slide-captcha__bar-text { position: absolute; top: 0; color: transparent; font-size: 12px; user-select: none; background: linear-gradient(90deg, var(--captcha-text-color) 0, var(--captcha-text-color) 40%, #fff 50%, var(--captcha-text-color) 60%, var(--captcha-text-color) 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: lazy-slide-captcha-unlock 3s infinite; }
  .lazy-slide-captcha__handler { position: absolute; top: 0; display: flex; align-items: center; justify-content: center; margin: 1px; border: 0; padding: 0; cursor: move; background: #fff; touch-action: none; }
  .lazy-slide-captcha__actions { display: flex; align-items: center; min-height: 20px; padding: 16px 20px 20px 0; color: rgb(80 80 80); line-height: 20px; }
  .lazy-slide-captcha__refresh { display: flex; align-items: center; gap: 4px; border: 0; padding: 0; color: rgb(80 80 80); background: transparent; cursor: pointer; font-size: 14px; }
  @keyframes lazy-slide-captcha-unlock { 0% { background-position: var(--captcha-pwidth) 0; } 100% { background-position: var(--captcha-width) 0; } }
  @keyframes lazy-slide-captcha-turn { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

  /* 移动端/H5 样式见 styles.h5.ts(SDK_STYLE_H5,@media 640px);
     此处仅 PC 样式,桌面不匹配 media query 不受影响。 */
`;
